import { useState } from "react";
import { ThumbsUp, ThumbsDown } from "../../icons";
import { apiCall } from "../../api/client";
import { help } from "../../api/contracts/help";
import { Button } from "../../button";
import { Card, CardContent } from "../../card";

export interface HelpFeedbackBlockProps {
  article_id: number;
}

/**
 * 👍 / 👎 buttons at the bottom of an article. Posts to
 * `/api/help/articles/record-feedback` which increments the right
 * counter and writes a `help_feedback_given` activity row.
 *
 * Local-only optimistic state — once the user clicks, the buttons
 * are replaced with a short thanks. Reload to vote again (the
 * server doesn't track per-user idempotency in v1; the schema is
 * append-only via the activities table).
 */
export function HelpFeedbackBlock({ article_id }: HelpFeedbackBlockProps) {
  const [pending, setPending] = useState<"helpful" | "unhelpful" | null>(null);
  const [done, setDone] = useState<"helpful" | "unhelpful" | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function send(value: "helpful" | "unhelpful") {
    if (pending || done) return;
    setPending(value);
    setError(null);
    try {
      await apiCall(help.articles.record_feedback, {
        body: { article_id, value },
      });
      setDone(value);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not record feedback");
    } finally {
      setPending(null);
    }
  }

  return (
    <Card>
      <CardContent className="space-y-3">
        <p className="type-body text-foreground">Was this helpful?</p>
        {done ? (
          <p className="type-body text-muted-foreground">
            {done === "helpful"
              ? "Thanks — glad it helped."
              : "Thanks — we&rsquo;ll work on this one."}
          </p>
        ) : (
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={pending !== null}
              onClick={() => send("helpful")}
              aria-label="Mark this article helpful"
            >
              <ThumbsUp aria-hidden className="size-3.5" />
              Yes
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={pending !== null}
              onClick={() => send("unhelpful")}
              aria-label="Mark this article unhelpful"
            >
              <ThumbsDown aria-hidden className="size-3.5" />
              No
            </Button>
          </div>
        )}
        {error ? (
          <p className="type-body text-destructive">{error}</p>
        ) : null}
      </CardContent>
    </Card>
  );
}
